import React, {
    createContext,
    ReactNode,
    useContext,
    useState,
    FC,
} from "react";
import { ConfirmModal } from "components/ConfirmModal";

interface ConfirmProviderProps {
    children: ReactNode;
}

interface ShowModalArgs {
    title: string;
    subtitle: string;
    onConfirm: () => void;
    okText?: string;
    cancelText?: string;
    onCancel?: () => void;
    children?: ReactNode | (() => ReactNode);
}

interface ConfirmModalContextType {
    showModal: (args: ShowModalArgs) => void;
    hideModal: () => void;
}

const ConfirmModalContext = createContext<ConfirmModalContextType | undefined>(
    undefined
);

export const useConfirmModal = () => {
    const context = useContext(ConfirmModalContext);
    if (!context) {
        throw new Error("useConfirmModal must be used within a ConfirmModalProvider");
    }
    return context;
};

export const ConfirmModalProvider: FC<ConfirmProviderProps> = ({ children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [modalState, setModalState] = useState<ShowModalArgs>({
        title: "",
        subtitle: "",
        onConfirm: () => {},
        okText: undefined,
        cancelText: undefined,
        onCancel: () => {},
        children: undefined,
    });

    const showModal = (args: ShowModalArgs) => {
        setModalState(args);
        setIsVisible(true);
    };

    const hideModal = () => {
        setIsVisible(false);
        setModalState({
            title: "",
            subtitle: "",
            onConfirm: () => {},
            okText: undefined,
            cancelText: undefined,
            onCancel: () => {},
            children: undefined,
        });
    };

    return (
        <ConfirmModalContext.Provider value={{ showModal, hideModal }}>
            {children}

            <ConfirmModal
                isVisible={isVisible}
                visibilityHandler={hideModal}
                title={modalState.title}
                subtitle={modalState.subtitle}
                onConfirm={modalState.onConfirm}
                onCancel={modalState.onCancel}
                okText={modalState.okText}
                cancelText={modalState.cancelText}
            >
                {typeof modalState.children === "function"
                    ? modalState.children()
                    : modalState.children}
            </ConfirmModal>
        </ConfirmModalContext.Provider>
    );
};
